import { useCallback, useMemo } from 'react';
import { createSearchParamsHook } from '@gonfalon/router/createSearchParamsHook';
import { z } from 'zod';

import type { MonthOption, UsageTabKey } from '../../types';
import { AggregationType, DimensionType, TimeRangeOption } from '../../types';
import { usageTabConfig } from '../constants/usageTabConfig';
import { getGranularity } from '../utils/getGranularity';

export type UsageDetailSearchParams = {
  timeRange?: TimeRangeOption;
  month?: string;
  aggregationType?: AggregationType;
  dimension?: DimensionType;
};

type DefaultSearchParams = {
  timeRange: TimeRangeOption;
  month: string;
  aggregationType: AggregationType;
};

type ResolvedUsageDetailSearchParams = {
  timeRange?: TimeRangeOption;
  month?: string;
  aggregationType: AggregationType;
  dimension?: DimensionType;
};

function parseUsageDetailSearchParams(searchParams: URLSearchParams): UsageDetailSearchParams {
  return {
    timeRange: (searchParams.get('time_range') as TimeRangeOption) ?? undefined,
    month: searchParams.get('month') ?? undefined,
    aggregationType: (searchParams.get('aggregation_type') as AggregationType) ?? undefined,
    dimension: (searchParams.get('group_by') as DimensionType) ?? undefined,
  };
}

function serializeUsageDetailSearchParams(params: UsageDetailSearchParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (params.timeRange) {
    searchParams.set('time_range', params.timeRange);
  }
  if (params.month) {
    searchParams.set('month', params.month);
  }
  if (params.aggregationType) {
    searchParams.set('aggregation_type', params.aggregationType);
  }
  if (params.dimension) {
    searchParams.set('group_by', params.dimension);
  }
  return searchParams;
}

const useRawUsageDetailSearchParams = createSearchParamsHook<UsageDetailSearchParams>({
  parse: parseUsageDetailSearchParams,
  serialize: serializeUsageDetailSearchParams,
});

function buildTabSchema(tab: UsageTabKey) {
  const config = usageTabConfig[tab];
  return z.object({
    timeRange: z
      .nativeEnum(TimeRangeOption)
      .optional()
      .refine((val) => val === undefined || config.granularities.includes(getGranularity(val)), {
        message: `timeRange is not allowed for tab "${tab}"`,
      }),
    month: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Must be in YYYY-MM format')
      .optional(),
    aggregationType: z
      .nativeEnum(AggregationType)
      .optional()
      .refine((val) => val === undefined || config.aggregationTypes.includes(val), {
        message: `aggregationType is not allowed for tab "${tab}"`,
      }),
    dimension: z
      .nativeEnum(DimensionType)
      .optional()
      .refine((val) => val === undefined || config.dimensions.includes(val), {
        message: `dimension is not allowed for tab "${tab}"`,
      }),
  });
}

type ResolveParamsOptions = {
  raw: UsageDetailSearchParams;
  defaults: {
    month: string;
    aggregationType: AggregationType;
    timeRange: TimeRangeOption;
  };
  monthOptions?: readonly MonthOption[];
};

function resolveUsageDetailSearchParams({
  raw,
  defaults,
  monthOptions,
}: ResolveParamsOptions): ResolvedUsageDetailSearchParams {
  const timeRange = raw.timeRange ?? defaults.timeRange;
  const dimension = raw.dimension;
  let aggregationType = raw.aggregationType ?? defaults.aggregationType;
  let month = raw.month;

  // Rule 1: Month is required for MONTH_TO_DATE time range
  if (timeRange === TimeRangeOption.MONTH_TO_DATE) {
    month ??= defaults.month;
    if (monthOptions && !monthOptions.some((option) => option.key === month)) {
      month = defaults.month;
    }
  } else {
    month = undefined;
  }

  // Rule 2: Monthly granularity requires MONTH_TO_DATE aggregation
  if (timeRange === TimeRangeOption.THREE_MONTH && aggregationType !== AggregationType.MONTH_TO_DATE) {
    aggregationType = AggregationType.MONTH_TO_DATE;
  }

  return { timeRange, month, aggregationType, dimension };
}

export const useUsageDetailSearchParams = (
  tab: UsageTabKey,
  defaults: DefaultSearchParams,
  monthOptions?: readonly MonthOption[],
): [
  ResolvedUsageDetailSearchParams,
  (updater: (prev: UsageDetailSearchParams) => UsageDetailSearchParams) => void,
  (params: UsageDetailSearchParams) => URLSearchParams,
] => {
  const [rawParams, setRawParams, serialize] = useRawUsageDetailSearchParams();

  const resolvedParams = useMemo(() => {
    const resolved = resolveUsageDetailSearchParams({ raw: rawParams, defaults, monthOptions });
    const result = buildTabSchema(tab).safeParse(resolved);
    if (!result.success) {
      return {
        month: defaults.month,
        aggregationType: defaults.aggregationType,
        dimension: undefined,
        timeRange: defaults.timeRange,
      };
    }

    return {
      month: result.data.month,
      aggregationType: result.data.aggregationType ?? defaults.aggregationType,
      dimension: result.data.dimension,
      timeRange: result.data.timeRange,
    };
  }, [rawParams, tab, defaults, monthOptions]);

  const setParams = useCallback(
    (updater: (prev: UsageDetailSearchParams) => UsageDetailSearchParams) => {
      setRawParams(updater(resolvedParams));
    },
    [resolvedParams, setRawParams],
  );

  return [resolvedParams, setParams, serialize];
};
